import React, { useState, useEffect } from 'react';
import { Plus, Edit2, X, Check, Shield, ToggleLeft, ToggleRight, Trash2, Key, Copy, Hash, Package, CreditCard } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const emptyForm = { username: '', full_name: '', email: '', phone: '', password: '', role: 'lco', operator_code: '' };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState('all');
  const [showPassModal, setShowPassModal] = useState(false);
  const [passUser, setPassUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [myCode, setMyCode] = useState('');
  const [showPkgModal, setShowPkgModal] = useState(false);
  const [pkgUser, setPkgUser] = useState(null);
  const [userPackages, setUserPackages] = useState([]);
  const [loadingPkgs, setLoadingPkgs] = useState(false);

  useEffect(() => { fetchUsers(); fetchMyCode(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/users');
      const allUsers = res.data.data || [];
      // Superadmin sees dist/lco only; dist/lco see their endusers too
      const filtered = allUsers.filter(u => u.role !== 'enduser' || me?.role !== 'superadmin');
      setUsers(filtered);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  };

  const fetchMyCode = async () => {
    try {
      const res = await api.get('/api/users/me');
      setMyCode(res.data.operator_code || '');
    } catch {}
  };

  const getRoleOptions = () => {
    if (me?.role === 'superadmin') return ['distributor', 'lco'];
    if (me?.role === 'distributor') return ['lco'];
    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        await api.put(`/api/users/${editUser.id}`, form);
        toast.success('User updated');
      } else {
        await api.post('/api/users', form);
        toast.success('User created');
      }
      setShowModal(false); setEditUser(null); setForm(emptyForm);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.message || 'Error'); }
  };

  const handleEdit = (u) => {
    setEditUser(u);
    setForm({ username: u.username, full_name: u.full_name || '', email: u.email || '', phone: u.phone || '', password: '', role: u.role, operator_code: u.operator_code || '' });
    setShowModal(true);
  };

  const handleToggle = async (u) => {
    try {
      await api.patch(`/api/users/${u.id}/toggle`);
      fetchUsers();
    } catch { toast.error('Failed'); }
  };
  const handleMarkPaid = async (u) => {
    if (!window.confirm(`Confirm you've received ₹3000 from ${u.username} for this platform billing cycle?`)) return;
    try {
      await api.put(`/api/users/${u.id}/mark-tenant-paid`);
      toast.success('Marked paid - next due in 30 days');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.username}?`)) return;
    try { await api.delete(`/api/users/${u.id}`); fetchUsers(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/users/${passUser.id}/reset-password`, { password: newPassword });
      toast.success('Password updated'); setShowPassModal(false); setNewPassword('');
    } catch { toast.error('Failed'); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success('Copied!'); };

  const openPackages = async (u) => {
    setPkgUser(u); setShowPkgModal(true); setLoadingPkgs(true);
    try {
      const res = await api.get('/api/packages/assignments/' + u.id);
      setUserPackages(res.data.data || []);
    } catch { setUserPackages([]); }
    setLoadingPkgs(false);
  };

  const togglePackage = async (pkg) => {
    try {
      if (pkg.is_assigned) {
        await api.delete('/api/packages/assign', { data: { user_id: pkgUser.id, package_id: pkg.id } });
      } else {
        await api.post('/api/packages/assign', { user_id: pkgUser.id, package_id: pkg.id });
      }
      setUserPackages(prev => prev.map(p => p.id === pkg.id ? {...p, is_assigned: !p.is_assigned} : p));
      toast.success(pkg.is_assigned ? 'Package removed' : 'Package assigned');
    } catch { toast.error('Failed to update'); }
  };

  const filteredUsers = users.filter(u => filter === 'all' || u.role === filter);
  const roleColor = { superadmin: '#FF4757', distributor: '#00B4D8', lco: '#00d4aa' };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px' }}>Users</h2>
          {myCode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 12px', width: 'fit-content' }}>
              <Hash size={14} color="var(--text-secondary)" />
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Your operator code:</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--accent)', letterSpacing: '2px' }}>{myCode}</span>
              <button onClick={() => copyCode(myCode)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <Copy size={14} />
              </button>
            </div>
          )}
        </div>
        {getRoleOptions().length > 0 && (
          <button className="btn btn-primary" onClick={() => { setShowModal(true); setEditUser(null); setForm({...emptyForm, role: getRoleOptions()[0]}); }}>
            <Plus size={16} /> Add User
          </button>
        )}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {(me?.role === 'superadmin' ? ['all', 'distributor', 'lco'] : me?.role === 'distributor' ? ['all', 'lco', 'enduser'] : ['all', 'enduser']).map(r => (
          <button key={r} onClick={() => setFilter(r)} className="btn" style={{
            padding: '6px 16px', fontSize: '13px',
            background: filter === r ? 'var(--accent)' : 'var(--card-bg)',
            color: filter === r ? '#000' : 'var(--text-primary)',
            border: '1px solid var(--border)'
          }}>
            {r === 'all' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1)}
            {r !== 'all' && <span style={{ marginLeft: '6px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', padding: '1px 6px', fontSize: '11px' }}>
              {users.filter(u => u.role === r).length}
            </span>}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? <div>Loading...</div> : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['User', 'Role', 'Operator Code', 'Contact', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: '600' }}>{u.full_name || u.username}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>@{u.username}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: roleColor[u.role] + '22', color: roleColor[u.role], padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {u.operator_code && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: '700', color: 'var(--accent)', fontSize: '15px', letterSpacing: '2px' }}>{u.operator_code}</span>
                        <button onClick={() => copyCode(u.operator_code)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                          <Copy size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: '13px' }}>{u.email || '-'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{u.phone || ''}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ color: (u.role === 'enduser' ? u.has_active_subscription : u.is_active) ? '#00d4aa' : '#FF4757', fontSize: '12px', fontWeight: '600' }}>
                      {u.role === 'enduser' ? (u.has_active_subscription ? 'Active' : 'No Subscription') : (u.is_active ? 'Active' : 'Inactive')}
                    </span>
                    {u.role === 'distributor' && (() => {
                      const overdue = u.tenant_next_due_date && new Date(u.tenant_next_due_date) < new Date();
                      return (
                        <div style={{ fontSize: '11px', marginTop: '4px', color: overdue ? '#FF4757' : (u.tenant_next_due_date ? '#8892B0' : '#F5A623'), fontWeight: overdue ? '700' : '400' }}>
                          {u.tenant_next_due_date
                            ? (overdue ? `⚠ Bill overdue since ${new Date(u.tenant_next_due_date).toLocaleDateString('en-IN')}` : `Paid until ${new Date(u.tenant_next_due_date).toLocaleDateString('en-IN')}`)
                            : 'Billing not yet tracked'}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {['distributor','lco'].includes(u.role) && (
                        <button onClick={() => openPackages(u)} className="btn btn-secondary" style={{ padding: '6px 10px', color: '#00B4D8' }} title="Assign Packages"><Package size={14} /></button>
                      )}
                      {me?.role === 'superadmin' && u.role === 'distributor' && (() => {
                        const overdue = u.tenant_next_due_date && new Date(u.tenant_next_due_date) < new Date();
                        const dueLabel = u.tenant_next_due_date
                          ? `Platform bill due: ${new Date(u.tenant_next_due_date).toLocaleDateString('en-IN')}${overdue ? ' (OVERDUE)' : ''}`
                          : 'Platform billing not yet tracked for this tenant';
                        return (
                          <button onClick={() => handleMarkPaid(u)} className="btn btn-secondary" style={{ padding: '6px 10px', color: overdue ? '#FF4757' : '#F5A623' }} title={dueLabel}>
                            <CreditCard size={14} />
                          </button>
                        );
                      })()}
                      <button onClick={() => handleEdit(u)} className="btn btn-secondary" style={{ padding: '6px 10px' }} title="Edit"><Edit2 size={14} /></button>
                      <button onClick={() => { setPassUser(u); setShowPassModal(true); }} className="btn btn-secondary" style={{ padding: '6px 10px' }} title="Reset Password"><Key size={14} /></button>
                      <button onClick={() => handleToggle(u)} className="btn btn-secondary" style={{ padding: '6px 10px' }}>
                        {u.is_active ? <ToggleRight size={14} color="#00d4aa" /> : <ToggleLeft size={14} color="#FF4757" />}
                      </button>
                      {me?.role === 'superadmin' && (
                        <button onClick={() => handleDelete(u)} className="btn btn-danger" style={{ padding: '6px 10px' }}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editUser ? 'Edit User' : 'Add User'}</div>
              <button className="close-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Username *</label>
                  <input className="form-control" value={form.username} onChange={e => setForm({...form, username: e.target.value})} required disabled={!!editUser} />
                </div>
                <div className="form-group">
                  <label>Full Name</label>
                  <input className="form-control" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select className="form-control" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                    {getRoleOptions().map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                </div>
                {!editUser && (
                  <div className="form-group">
                    <label>Password *</label>
                    <input className="form-control" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={6} />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={16} />{editUser ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPassModal && passUser && (
        <div className="modal-overlay" onClick={() => setShowPassModal(false)}>
          <div className="modal" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Reset Password</div>
              <button className="close-btn" onClick={() => setShowPassModal(false)}><X size={20} /></button>
            </div>
            <div style={{ background: 'rgba(79,110,247,0.1)', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Resetting password for</div>
              <div style={{ fontSize: '15px', fontWeight: '600' }}>{passUser.full_name || passUser.username}</div>
            </div>
            <form onSubmit={handlePasswordReset}>
              <div className="form-group">
                <label>New Password *</label>
                <input className="form-control" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} placeholder="Minimum 6 characters" />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowPassModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Key size={16} />Update Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Package Assignment Modal */}
      {showPkgModal && pkgUser && (
        <div className="modal-overlay" onClick={() => setShowPkgModal(false)}>
          <div className="modal" style={{ maxWidth: '480px', maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><Package size={16}/> Assign Packages - {pkgUser.username}</div>
              <button className="close-btn" onClick={() => setShowPkgModal(false)}><X size={20}/></button>
            </div>
            <div className="modal-body" style={{ padding: '16px', maxHeight: '55vh', overflowY: 'auto' }}>
              {loadingPkgs ? <div style={{textAlign:'center',padding:'24px'}}>Loading...</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {userPackages.length === 0 && <div style={{color:'var(--text-secondary)',textAlign:'center',padding:'24px'}}>No packages available</div>}
                  {userPackages.map(pkg => (
                    <div key={pkg.id}
                      style={{ display:'flex',alignItems:'center',gap:'12px',padding:'12px',borderRadius:'8px',cursor:'pointer',background:pkg.is_assigned?'rgba(0,180,216,0.1)':'var(--card-bg)',border:'1px solid '+(pkg.is_assigned?'rgba(0,180,216,0.3)':'var(--border)') }}
                      onClick={() => togglePackage(pkg)}>
                      <div style={{ width:'20px',height:'20px',borderRadius:'4px',background:pkg.is_assigned?'#00B4D8':'transparent',border:'2px solid '+(pkg.is_assigned?'#00B4D8':'var(--border)'),display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                        {pkg.is_assigned && <Check size={12} color="#000"/>}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:'600',fontSize:'14px' }}>{pkg.name}</div>
                        <div style={{ fontSize:'12px',color:'var(--text-secondary)' }}>{pkg.validity_label||'1 Month'} - {pkg.channel_count} channels</div>
                      </div>
                      {pkg.is_assigned && <span style={{ fontSize:'11px',color:'#00B4D8',fontWeight:'600' }}>ASSIGNED</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowPkgModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
